import swLog from "@similarweb/sw-log";
import { BaseTracker } from "./BaseTracker";
import {
  CustomDimensions,
  ICustomDimensionData,
  IDimensions,
  TrackModel,
  FieldNameToDimensionNumMap,
  TrackingEvent,
  CustomAttribute,
  Primitive,
  SerializableObject,
} from "./ITrack";
import { tryParse } from "../utils/TryParse";
export abstract class MatomoTracker extends BaseTracker {
  private readonly maxRetriesNumber = 10;
  private readonly minRetriesNumberForLogging = 3;
  private readonly timeToWaitBetweenRetries = 1000;

  private customUrl = "";

  // Input example:
  // trackId = "call_id= 17rq3ui"
  protected trackEventInternal({ category, action, name, trackId, location, value }: TrackModel): void {
    let cid = "";
    // in case there is call_id we'll use it as CID
    if (trackId && trackId.includes("call_id=")) {
      cid = trackId.split("=")[1].trim();
    }
    name = [name, location].filter((a) => a).join("/");
    this.resilientTrackEvent(category instanceof Array ? category.filter((a) => a).join("/") : category, action, name, value, cid);
  }

  protected setCustomDimensionsWithoutOverride(
    customAttributes: Record<CustomAttribute, Primitive>,
    customDataDimensionID: number,
    customDimensionValue: SerializableObject | string,
  ) {
    const customAttributesKeys = Object.keys(customAttributes);
    const newCustomDimensionValue =
      typeof customDimensionValue === "object"
        ? customAttributesKeys.reduce((result, key) => {
            const superPropertyValue = customDimensionValue[key];
            if (superPropertyValue) {
              this.propertyAlreadyExistsWarn(key, superPropertyValue);
              return result;
            }
            return { ...result, [key]: customAttributes[key] };
          }, customDimensionValue)
        : customDimensionValue;
    this.setCustomDimension(customDataDimensionID, newCustomDimensionValue);
  }

  protected dispatchTrackEventInternal(
    event: TrackingEvent,
    action: string,
    eventName: string,
    customAttributes: Record<CustomAttribute, Primitive>,
  ) {
    if (!this.isMatomoLoaded()) return false;
    const customDataDimensionID = this.getDimensions().custom_data;
    const customDimensionValue = this.getCustomDimensionValue(customDataDimensionID);

    this.setCustomDimensionsWithoutOverride(customAttributes, customDataDimensionID, customDimensionValue);

    const { eventName: name, trackId, location } = this.toInternalEventConvention(eventName);
    this.trackEventInternal({
      category: event,
      trackId,
      location: location?.join("/"),
      action,
      name,
    });

    // reset the initial dimension value
    this.setCustomDimension(customDataDimensionID, customDimensionValue);
  }

  private resilientTrackEvent(category: string, action: string, name: string, value?: number, cid?: string, retryNumber = 1) {
    const success = this.tryTrackEvent(category, action, name, cid, value);

    if (success) {
      if (retryNumber > this.minRetriesNumberForLogging) {
        swLog.serverLogger(
          `[Matomo] Track event succeeded, retry number: ${retryNumber}. 
          category: ${category},
          action: ${action},
          name: ${name},
          value: ${value}.`,
          null,
          "Info",
          "Matomo",
        );
      }
      return;
    }

    if (retryNumber < this.maxRetriesNumber) {
      setTimeout(
        function () {
          this.resilientTrackEvent(category, action, name, value, cid, retryNumber + 1);
        }.bind(this),
        this.timeToWaitBetweenRetries,
      );
    } else {
      swLog.serverLogger(
        `[Matomo] Failed to track event.
        category: ${category},
        action: ${action},
        name: ${name},
        value: ${value}.`,
        null,
        "Debug",
        "Matomo",
      );
    }
  }

  private tryTrackEvent(category: string, action: string, name: string, cid: string, value?: number): boolean {
    if (!this.isMatomoLoaded()) return false;
    try {
      if (cid === null || cid === undefined || cid === "") {
        cid = this.generateId();
      }
      this.setCustomDimension(24, { cid: cid });
      this.paq().push(["trackEvent", category, action, name, value]);
      this.getBuffer().add(["trackEvent", category, action, name, value, this.customUrl]);
      return true;
    } catch (e) {
      swLog.serverLogger(
        `[Matomo] Error in Matomo analytics (event). ${e.message}.
        category: ${category},
        action: ${action},
        name: ${name},
        value: ${value}.`,
        e,
        "Debug",
        "Matomo",
      );
      return false;
    }
  }

  protected trackPageViewInternal(customDimsData: ICustomDimensionData): void {
    this.resilientTrackPageView(customDimsData);
  }

  private resilientTrackPageView(customDimsData: ICustomDimensionData, retryNumber = 1) {
    const success = this.tryTrackPageView(customDimsData);

    if (success) {
      if (retryNumber > this.minRetriesNumberForLogging) {
        swLog.serverLogger(
          `[Matomo] Track page view succeeded, retry number: ${retryNumber}. data: ${JSON.stringify(customDimsData)}.`,
          null,
          "Info",
          "Matomo",
        );
      }
      return;
    }

    if (retryNumber < this.maxRetriesNumber) {
      setTimeout(
        function () {
          this.resilientTrackPageView(customDimsData, retryNumber + 1);
        }.bind(this),
        this.timeToWaitBetweenRetries,
      );
    } else {
      swLog.serverLogger(`[Matomo] Failed to track page view. data: ${JSON.stringify(customDimsData)}.`, null, "Debug", "Matomo");
    }
  }

  private tryTrackPageView(customDimsData: ICustomDimensionData): boolean {
    if (!this.isMatomoLoaded()) return false;
    try {
      this.paq().push(["setCustomUrl", window.location.href]);
      this.setCustomDimensions(customDimsData);
      this.paq().push(["trackPageView"]);
      return true;
    } catch (e) {
      swLog.serverLogger(
        `[Matomo] Error in Matomo analytics (page view). ${e.message}. data: ${JSON.stringify(customDimsData)}`,
        e,
        "Debug",
        "Matomo",
      );
      return false;
    }
  }

  protected healthCheckInternal(): void {
    if (!this.isMatomoLoaded()) {
      swLog.serverLogger("[Matomo] Matomo is not loaded", null, "Debug", "Matomo");
    }
  }

  /**
   * getter for the native tracker
   */
  protected abstract paq(): any[];

  /**
   * should the tracker be buffered
   */
  protected abstract shouldBuffer(): boolean;

  /**
   * getter for the search params object
   */
  protected abstract searchParams(): any;

  /**
   * getter for the custom dimensions ids
   */
  protected getDimensions(): IDimensions {
    return CustomDimensions;
  }

  protected getFieldNameToDimensionNumMap(): IDimensions {
    return FieldNameToDimensionNumMap;
  }

  private isMatomoLoaded() {
    const paq = this.paq();
    return paq && !(Array.isArray(paq) && paq.length > 5);
  }

  private setCustomDimension(customDimensionId: any, customDimensionValue: any): void {
    this.paq().push([
      "setCustomDimension",
      customDimensionId,
      this.formatValue(this.mergeDimensions(customDimensionId, customDimensionValue)),
    ]);
  }

  protected getCustomDimensionValue(customDimensionId): SerializableObject | string {
    let customDimension;
    this.paq().push([
      function () {
        customDimension = this.getCustomDimension(customDimensionId);
      },
    ]);
    return tryParse(customDimension);
  }

  private formatValue(customDimensionValue): string {
    return typeof customDimensionValue === "object" ? JSON.stringify(customDimensionValue) : customDimensionValue;
  }

  private deleteCustomDimension(customDimensionId): void {
    this.paq().push(["deleteCustomDimension", customDimensionId]);
  }

  private setCustomDimensions(customDimsData: ICustomDimensionData): void {
    const resultMap = {};
    const flattenResult = this.flattenObject(customDimsData);
    const fieldNameToDimensionNumMap = this.getFieldNameToDimensionNumMap();

    // Iterate over the flatten object and collect only the fields we need (according to FieldNameToDimensionNumMap)
    for (const [key, value] of Object.entries(flattenResult)) {
      const dimensionNum = fieldNameToDimensionNumMap[key];

      // Skip if dimension not found or no values
      if (dimensionNum === undefined || value === null || value === "") {
        continue;
      }

      if (!resultMap[dimensionNum]) {
        // Init resultMap with new key
        resultMap[dimensionNum] = {};
      }

      resultMap[dimensionNum][key] = value;
    }

    // Push the results to paq
    for (const dimensionNum in resultMap) {
      this.setCustomDimension(dimensionNum, resultMap[dimensionNum]);
    }
  }
  /**
   * Merges a custom dimension object retrieved by its dimension number with an existing object.
   *
   * @param {string} dimNum - The identifier for the custom dimension to retrieve.
   * @param {object} currentDimValue - The current object to merge with the custom dimension.
   * @returns {object} - The merged object if both values are objects, otherwise returns the
   *                     `currentDimValue`.
   */
  private mergeDimensions(dimNum: any, currentDimValue: any) {
    const dimValue: string | SerializableObject = this.getCustomDimensionValue(dimNum);

    if (dimValue) {
      if (typeof dimValue === "object" && typeof currentDimValue === "object") {
        // Merge the two objects
        return { ...dimValue, ...currentDimValue };
      } else {
        return currentDimValue;
      }
    }
    return currentDimValue;
  }

  /**
   * Recursively flattens an object, transforming nested objects into a flat structure.
   *
   * @param {Object} obj - The input object to be flattened.
   * @returns {Object} A new object representing the flattened structure of the input object.
   */
  private flattenObject(obj: ICustomDimensionData) {
    return Object.keys(obj).reduce((res, key) => {
      if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(res, this.flattenObject(obj[key]));
      } else {
        res[key] = obj[key];
      }
      return res;
    }, {});
  }
}
